import { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Dialog, Table, withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import { ConnectedProps, connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import translations from 'src/internationalisation/translation-map.json';
import { getAllRigOperationTemplates } from 'src/store/entities/company-settings/company-settings';
import type { TRootState } from 'src/store/store-types';
import { IActivity, IRigOperationTemplate } from 'src/common/interfaces/rig-operations-template.interfaces';
import { selectUnitByQuantity } from 'store/entities/unit-templates/selectors.ts';
import { QuantitiesTypes } from 'src/enums/unit-templates';
import { estimateTypes, distributionTypes } from 'src/enums/tasks';
import { bulkImportTasks } from 'src/store/entities/activity-model/activity-model';
import { convertInputUnits, convertToStorageUnits } from 'src/common/units/units';
import { selectExtendedLinkFields } from 'src/store/entities/activity-model/selectors';
import { Operation } from 'src/common/interfaces/activity-model';
import { importActivities } from 'src/store/entities/templates/templates';
import { ITemplate } from 'src/common/interfaces/templates.interfaces';

type TImportFromTemplateModal = PropsFromRedux & {
  handleTaskImportModal: (isOpen: boolean) => void;
  rigOperationTemplates: IRigOperationTemplate[];
  isLinked: boolean;
  activeOperation: Operation;
  parentId?: string;
  projectId?: string;
  templates?: ITemplate[];
  companyId?: string;
};

const ImportFromTemplateModal = ({
  handleTaskImportModal,
  projectId,
  rigOperationTemplates,
  isLinked,
  durationUnit,
  ROPUnit,
  getAllRigOperationTemplates,
  linkedFields,
  activeOperation,
  bulkImportTasks,
  parentId,
  importActivities,
  templates,
  companyId,
}: TImportFromTemplateModal) => {
  const { t } = useTranslation();

  useEffect(() => {
    getAllRigOperationTemplates();
  }, [getAllRigOperationTemplates]);

  const onClose = () => handleTaskImportModal(false);

  const [selectedActivities, setSelectedActivities] = useState<any[]>([]);
  const [filters, setFilters] = useState<Record<string, string>>({});

  const allActivities = useMemo(() => {
    return rigOperationTemplates.flatMap((template) =>
      template.activities.map((activity) => ({
        ...(activity as any),
        templateName: template.name,
        templateId: template.rigOperationTemplateId,
      })),
    );
  }, [rigOperationTemplates]);

  const filteredActivities = useMemo(() => {
    return allActivities.filter((activity) => {
      return Object.entries(filters).every(([key, value]) => {
        if (!value) return true;
        const activityValue = String(activity[key] || '').toLowerCase();
        return activityValue.includes(String(value).toLowerCase());
      });
    });
  }, [allActivities, filters]);

  const filteredIds = useMemo(
    () => new Set(filteredActivities.map((a) => a.rigOperationTemplateActivityId)),
    [filteredActivities],
  );

  const isAllFilteredSelected =
    filteredActivities.length > 0 &&
    filteredActivities.every((activity) =>
      selectedActivities.some(
        (selected) => selected.rigOperationTemplateActivityId === activity.rigOperationTemplateActivityId,
      ),
    );

  const selectAllCell = {
    key: 'selectAll',
    value: '',
    placeholder: '',
    onChange: () => {
      if (isAllFilteredSelected) {
        setSelectedActivities((prev) => prev.filter((item) => !filteredIds.has(item.rigOperationTemplateActivityId)));
      } else {
        setSelectedActivities((prev) => {
          const prevIds = new Set(prev.map((p) => p.rigOperationTemplateActivityId));
          const toAdd = filteredActivities.filter((item) => !prevIds.has(item.rigOperationTemplateActivityId));
          return [...prev, ...toAdd];
        });
      }
    },
    type: 'CheckBox',
    checked: isAllFilteredSelected,
  };

  const headings = [
    { name: 'name', label: t(translations.name) },
    { name: 'templateName', label: t(translations.template) },
    { name: 'estimateType', label: t(translations.estimateType) },
    { name: 'distribution', label: t(translations.activityModel_distributionType) },
    { name: 'min', label: t(translations.minimum) },
    { name: 'most', label: t(translations.mostLikely) },
    { name: 'max', label: t(translations.maximum), disabled: true },
  ];

  const dataHeaderCells = headings.map((heading) => ({
    key: heading.name,
    value: heading.label,
    type: 'Text',
  }));

  const dataFilterCells = headings.map((heading) => ({
    key: `filter-${heading.name}`,
    value: filters[heading.name] || '',
    placeholder: `Filter ${heading.label}`,
    onChange: (e: any) => setFilters({ ...filters, [heading.name]: e.target.value }),
    type: 'Input',
  }));

  dataHeaderCells.unshift({ key: 'select', value: '', type: 'Text' });
  dataFilterCells.unshift(selectAllCell);

  const isCellDisabled = (name: string, activity: IActivity): boolean => {
    const { distribution } = activity;
    return (
      ((name === 'min' || name === 'max') && distribution === distributionTypes.SPIKE) ||
      (name === 'most' && distribution === distributionTypes.UNIFORM)
    );
  };

  const rows = filteredActivities.map((activity) => {
    const checked = selectedActivities.some(
      (item) => item.rigOperationTemplateActivityId === activity.rigOperationTemplateActivityId,
    );

    return {
      cells: [
        {
          onChange: () => {
            if (checked) {
              setSelectedActivities((prev) =>
                prev.filter((item) => item.rigOperationTemplateActivityId !== activity.rigOperationTemplateActivityId),
              );
            } else {
              setSelectedActivities((prev) => [...prev, activity]);
            }
          },
          type: 'CheckBox',
          checked,
        },
        {
          name: 'name',
          value: activity.name,
        },
        {
          name: 'templateName',
          value: activity.templateName,
        },
        {
          name: 'estimateType',
          value: activity.estimateType === estimateTypes.OPERATION_SPEED ? ROPUnit : durationUnit,
        },
        {
          name: 'distribution',
          value: activity.distribution,
        },
        {
          name: 'min',
          value: isCellDisabled('min', activity) ? '' : activity.min,
          style: {
            backgroundColor: isCellDisabled('min', activity) ? 'var(--color-background-disabled)' : 'transparent',
            minWidth: '100px',
          },
        },
        {
          name: 'most',
          value: isCellDisabled('most', activity) ? '' : activity.most,
          style: {
            backgroundColor: isCellDisabled('most', activity) ? 'var(--color-background-disabled)' : 'transparent',
            minWidth: '100px',
          },
        },
        {
          name: 'max',
          value: isCellDisabled('max', activity) ? '' : activity.max,
          style: {
            backgroundColor: isCellDisabled('max', activity) ? 'var(--color-background-disabled)' : 'transparent',
            minWidth: '100px',
          },
        },
      ],
    };
  });

  const tableConfig = {
    testId: 'import-template-modal-table',
    headers: [
      {
        cells: dataHeaderCells,
      },
      {
        cells: dataFilterCells,
      },
    ],
    rows,
  };

  const onImport = (activities: IActivity[], isLinked: boolean) => {
    const activitiesToImport = activities.map((activity) => {
      const isOperationSpeed = activity.estimateType === estimateTypes.OPERATION_SPEED;
      const convertedLength = convertToStorageUnits(activity, ['from', 'to'], {
        from: 'm',
        to: 'm',
      });
      const convertedTask = convertInputUnits(
        convertedLength,
        ['min', 'most', 'max'],
        isOperationSpeed ? ROPUnit || 'm/h' : durationUnit || 'h',
        isOperationSpeed ? 'm/h' : 'h',
      );
      return convertedTask;
    }) as IActivity[];
    if (projectId) {
      bulkImportTasks({
        operationId: activeOperation.operationId,
        projectId,
        parentId,
        linkedFields,
        tasks: activitiesToImport,
        isLinked,
      });
    } else if (companyId && templates) {
      importActivities(activitiesToImport, templates, companyId, isLinked, parentId);
    }

    handleTaskImportModal(false);
  };

  return (
    <Modal visible onEscape={onClose}>
      <Dialog
        dialog={{
          heading: t(translations.activityModel_useTemplate),
          width: '70vw',
          scroll: true,
          content: (
            <>
              <Table table={tableConfig} />
            </>
          ),
          footer: (
            <>
              <Button
                testId="import-template-button-confirm"
                label={'Import'}
                colored
                disabled={!selectedActivities.length}
                onClick={() => onImport(selectedActivities, isLinked)}
              />
              <Button label={t(translations.cancel)} onClick={() => handleTaskImportModal(false)} />
            </>
          ),
          onClose,
        }}
      />
    </Modal>
  );
};

const mapStateToProps = (state: TRootState) => {
  const { entities } = state;
  const { rigOperationTemplates } = entities.companySettings;

  return {
    rigOperationTemplates: rigOperationTemplates || [],
    ROPUnit: selectUnitByQuantity(entities, QuantitiesTypes.ROP),
    durationUnit: selectUnitByQuantity(entities, QuantitiesTypes.Duration),
    linkedFields: selectExtendedLinkFields(state),
  };
};

const mapDispatchToProps = {
  getAllRigOperationTemplates,
  bulkImportTasks,
  importActivities,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Container = withErrorBoundary(connector(ImportFromTemplateModal), {
  style: { position: 'absolute', bottom: 0, zIndex: 1000 },
});

export { Container as ImportFromTemplateModal };
